import { createFeatureSelector, createSelector } from '@ngrx/store';
import {distinct, groupBy, precisionRound, sum} from '@common/lang/lang';
import { ArrearsGroupedModel } from '../models/arrears-grouped-model';
import { QueryBuilderConfig, RuleSet, Rule } from 'ngx-angular-query-builder';
import { ArrearsModel, ArrearsTable } from '../models/arrears.model';
import { ArrearsState, arrearsWidgetAdapter, ArrearsInstance } from '../reducers/arrears.reducers';
import { ArrearsFields } from '../arrears-fields.provider';
import { ArrearsFilterFieldProvider } from '../filter/arrears-filter-field-provider';
import * as filters from '@common/filter/standard-filter-strategy';
import { QueryBuilderConfigProvider } from '@common/filter/query-builder-config-provider';
import { MathematicalHelperService } from '@common/services/mathematical-helper-service';
import * as widgetConfigSelectors from '@widget-configs/selectors/widget-configs';
import { WidgetConfiguration } from '@widget-configs/models/widget-configuration';
import * as filterSelectors from '@common/filter/selectors/filter.selectors';
import * as filterReducers from '@common/filter/reducers/filter.reducers';
import * as filterUtils from '@common/filter/filter-utils';
import * as arrearsDrillDownSelectors from './arrears-drilldown.selectors';
import { ArrearsDrillDownInstance } from '../reducers/arrears-drilldown.reducer';
import * as lodash from 'lodash';
import { AdminArrearsFilterFieldProvider } from '../filter/admin-arrears-filter-field-provider';


export const selectArrearsState = createFeatureSelector<ArrearsState>('arrears');

// Refactoring needs to be done to unpick the ArrearsFilterFieldProvider from the selectors
// first step is providing a callback to allow the construction to take place outside
export type ArrearsFilterFieldProviderFactoryCallback = (arrears: Array<ArrearsModel>, arrearsFields: ArrearsFields) => ArrearsFilterFieldProvider

export function makeArrearsFilterFieldProviderCallback(effectiveProduct: string, currentDashboard: string): ArrearsFilterFieldProviderFactoryCallback {

  return (arrears: Array<ArrearsModel>, arrearsFields: ArrearsFields) => {
    return new ArrearsFilterFieldProvider(arrears, arrearsFields, effectiveProduct, currentDashboard);
  }

}

export const {
  selectIds,
  selectEntities,
  selectAll: selectAllDiaryWidgetsData,
  selectTotal,
} = arrearsWidgetAdapter.getSelectors(selectArrearsState);

export const selectEntityByWidgetId = (widgetId: string) => createSelector(selectEntities, (entities) => entities[widgetId.toString()]);

function formatDataName(dataName: string): string {
  if (dataName == undefined || dataName == '' || dataName == 'null') {
    return 'None';
  }
  return dataName;
}


export function getArrearsTable(widgetId: string, widgetConfigId: number, arrearsFields: ArrearsFields, filterFieldCallback: ArrearsFilterFieldProviderFactoryCallback) {
  return createSelector(
    [selectArrearsState, selectEntityByWidgetId(widgetId.toString()), getFilter(widgetId, widgetConfigId)],
    (arrearsState: ArrearsState, arrearsInstance: ArrearsInstance, filter: RuleSet) => {
      const result = new ArrearsTable([], []);

      if (arrearsState && arrearsState.arrears && arrearsState.arrears.ArrearsTable) {

        const mathematicalHelperService: MathematicalHelperService = new MathematicalHelperService;
        const arrearsFilterFieldProvider = filterFieldCallback(arrearsState.arrears.ArrearsTable, arrearsFields);
        const arrearsFilteredByCurrency = arrearsState.arrears.ArrearsTable.filter(a => a.CurrencyCode === arrearsInstance.selectedCurrency);
        const filterStrategy = new filters.StandardFilterStrategy<ArrearsModel>(arrearsFilterFieldProvider);
        const filteredArray = filterStrategy.applyFilter(arrearsFilteredByCurrency, filter);

        let distinctValuesInGroupByField1: Array<string> = [];
        distinctValuesInGroupByField1 = distinct<string, ArrearsModel>(filteredArray, arrearsInstance.groupByField1);
        if (arrearsInstance.groupByField1 === arrearsFields.age.fieldName) {
          distinctValuesInGroupByField1 = sortAges(distinctValuesInGroupByField1, arrearsFields);
        } else {
          distinctValuesInGroupByField1.sort();
        }

        const balancesGroupedByField2 = groupBy(filteredArray, arrearsInstance.groupByField2);
        result.xAxisLabels.push(arrearsInstance.column1Header);
        result.xAxisLabels.push(...distinctValuesInGroupByField1);
        result.xAxisLabels.push('Total');

        for (const name in balancesGroupedByField2) {

          const itemToAdd: any = {};
          itemToAdd[arrearsInstance.column1Header] = name;

          let rowRunningTotal = 0;

          distinctValuesInGroupByField1.forEach(distinctName => {

            const itemsFilteredByDistinctName = balancesGroupedByField2[name].filter(property => {
              return property[arrearsInstance.groupByField1] === distinctName;
            });

            if (itemsFilteredByDistinctName.length > 0) {
              const summedValue = mathematicalHelperService.precisionRound(sum(itemsFilteredByDistinctName, 'Total'), 2);
              itemToAdd[distinctName] = summedValue;
              rowRunningTotal += summedValue;
            } else {
              itemToAdd[distinctName] = 0;
            }
            itemToAdd.Total = mathematicalHelperService.precisionRound(rowRunningTotal, 2);
          });

          result.rows.push(itemToAdd);
        }

        return result;
      }

      return result;
    }
  );
}

export function getTotalRow(widgetId: string, widgetConfigId: number, arrearsFields: ArrearsFields, filterFieldCallback: ArrearsFilterFieldProviderFactoryCallback) {
  return createSelector(
    [getArrearsTable(widgetId, widgetConfigId, arrearsFields, filterFieldCallback)],
    (arrearsTableData) => {
      const totalRow: any = {};
      if (arrearsTableData?.xAxisLabels) {
        arrearsTableData.xAxisLabels.forEach(xAxisLabel => {
          const summedValue = precisionRound(sum(arrearsTableData.rows, xAxisLabel), 2);
          totalRow[xAxisLabel] = summedValue;
        });
      }
      return totalRow;
    }
  );
}

export function getAdminFilterConfig() {
  return createSelector(
    [
      selectArrearsState
    ],
    (arrearsState: ArrearsState) => {
      const adminData = arrearsState.adminData;
      if (adminData) {
        const arrearsFields = new ArrearsFields();

        const adminArrearsFilterFieldProvider = new AdminArrearsFilterFieldProvider(
          adminData.PropertyTypes,
          adminData.PropertyGroups,
          adminData.PropertyLocations,
          adminData.SubLedgers,
          arrearsFields);
        const configBuilder = new QueryBuilderConfigProvider(adminArrearsFilterFieldProvider);
        const config = configBuilder.getQueryBuilderConfig() as QueryBuilderConfig;
        return config;
      }
      return undefined as QueryBuilderConfig;
    });
}

export function getFilteredArrears(widgetId: string, widgetConfigId: number, arrearsFields: ArrearsFields, filterFieldCallback: ArrearsFilterFieldProviderFactoryCallback) {
  return createSelector(
    [
      selectArrearsState, selectEntityByWidgetId(widgetId.toString()),
      getFilter(widgetId, widgetConfigId)
    ],
    (arrearsState: ArrearsState, arrearsInstance: ArrearsInstance, filter: RuleSet) => {
      const applyFilter = arrearsState &&
        arrearsState.arrears &&
        arrearsState.arrears.ArrearsTable &&
        arrearsInstance &&
        filter &&
        arrearsInstance.groupByField1;
      if (applyFilter) {
        const arrearsFilterFieldProvider = filterFieldCallback(arrearsState.arrears.ArrearsTable, arrearsFields);
        const arrearsFilteredByCurrency = arrearsState.arrears.ArrearsTable.filter(a => a.CurrencyCode === arrearsInstance.selectedCurrency);
        const filterStrategy = new filters.StandardFilterStrategy<ArrearsModel>(arrearsFilterFieldProvider);
        return filterStrategy.applyFilter(arrearsFilteredByCurrency, filter);
      }
      return [];
    });
}

export function getUserFilter(widgetId: string) {
  return createSelector([filterSelectors.selectByWidgetId(widgetId), selectEntityByWidgetId(widgetId.toString())],
    (filter: filterReducers.WidgetFilterInstance, arrearsInstance: ArrearsInstance) => {
      if (filter && arrearsInstance && filter.filter) {
        return filter.filter;
      }
      return filterUtils.getDefaultFilter();
    });
}

function getWidgetConfigFilter(widgetConfigId: number) {
  return createSelector([widgetConfigSelectors.selectEntityByWidgetConfigId(widgetConfigId)],
    (widgetConfiguration: WidgetConfiguration) => {
      if (widgetConfiguration != undefined) {
        return widgetConfiguration.filter;
      }
      return null;
    });
}


export function getFilter(widgetId: string, widgetConfigId: number) {
  return createSelector(
    [
      getUserFilter(widgetId),
      arrearsDrillDownSelectors.selectDrillDownState(widgetId.toString()),
      getWidgetConfigFilter(widgetConfigId)
    ],
    (
      filter: RuleSet,
      drillDownInstance: ArrearsDrillDownInstance,
      widgetConfigFilter: RuleSet
    ) => {

      const combinedFilter = filterUtils.getDefaultFilter();
      combinedFilter.condition = 'and';
      const combine = (filterToCombine: RuleSet | Rule) => combinedFilter.rules.push(lodash.cloneDeep(filterToCombine));

      if (widgetConfigFilter) {
        combine(widgetConfigFilter);
      }

      if (filter) {
        combine(filter);
      }

      if (drillDownInstance && drillDownInstance.inDrillDownMode) {
        combine(drillDownInstance.drillDownParams.rule);
      }

      return combinedFilter;

    });
}

function createGroupArrears(array: ArrearsModel[], prop: string, groupedByName: string, arrearsFields: ArrearsFields): ArrearsGroupedModel {
  const group = groupBy(array, prop);

  const model = {
    TotalValues: [],
    DataNames: [],
    StackedDataValues: [],
    SetNames: ['Total'],
    GroupedBy: groupedByName,
  } as ArrearsGroupedModel;

  let keys: Array<string> = [];
  keys = Object.keys(group);
  if (prop === arrearsFields.age.fieldName) {
    keys = sortAges(keys, arrearsFields);
  } else {
    keys.sort();
  }

  for (let i = 0; i < keys.length; ++i) {
    model.DataNames.push(formatDataName(keys[i]));
    const value = sum(group[keys[i]], 'Total');
    model.TotalValues.push(value);
  }
  model.StackedDataValues.push(model.TotalValues);
  return model;
}

export function sortAges(agesToBeSorted: Array<string>, arrearsFields: ArrearsFields): Array<string> {
  const sortOrder = arrearsFields.agedDaysFields.map(f => f.label);
  return sortOrder.filter(v => agesToBeSorted.includes(v));
}

export function getArrears(widgetId, widgetConfigId: number, arrearsFields: ArrearsFields, filterFieldCallback: ArrearsFilterFieldProviderFactoryCallback) {
  return createSelector(
    [getFilteredArrears(widgetId, widgetConfigId, arrearsFields, filterFieldCallback), selectEntityByWidgetId(widgetId)],
    (filteredArray: Array<ArrearsModel>, arrearsInstance: ArrearsInstance) => {
      if (filteredArray &&
        arrearsInstance &&
        arrearsInstance.groupByField1) {
        return createGroupArrears(filteredArray,
          arrearsInstance.groupByField1,
          arrearsInstance.column1Header,
          arrearsFields);
      }
      return undefined;
    });
}


export function getQueryBuilderConfig(widgetId: string, widgetConfigId: number, arrearsFields: ArrearsFields, filterFieldCallback: ArrearsFilterFieldProviderFactoryCallback) {
  return createSelector(
    [selectArrearsState],
    (arrearsState: ArrearsState) => {
      if (arrearsState && arrearsState.arrears && arrearsState.arrears.ArrearsTable) {
        const arrearsFilterFieldProvider = filterFieldCallback(arrearsState.arrears.ArrearsTable, arrearsFields);
        const arrearsQueryConfigBuilder = new QueryBuilderConfigProvider(arrearsFilterFieldProvider);
        return arrearsQueryConfigBuilder.getQueryBuilderConfig() as QueryBuilderConfig;
      }

      return { fields: {} } as QueryBuilderConfig;
    }
  );
}


export function getArrearsLoading(widgetId) {
  return createSelector(
    [selectArrearsState],
    (arrearsState: ArrearsState) => {
      return arrearsState.loading;
    }
  );
}

export function getArrearsError(widgetId) {
  return createSelector(
    [selectArrearsState],
    (arrearsState: ArrearsState) => {
      return arrearsState.error;
    }
  );
}

export const getArrearsDate = createSelector(
  [selectArrearsState],
  (arrearsState: ArrearsState) => {
    if (arrearsState && arrearsState.arrears && arrearsState.arrears.AsAtDate) {
      return arrearsState.arrears.AsAtDate;
    }
    return undefined;
  }
);

export function getSysAdminFilterStrings(widgetConfigId: number) {
  return createSelector(
    [widgetConfigSelectors.selectEntityByWidgetConfigId(widgetConfigId)],
    (widgetConfiguration: WidgetConfiguration) => {
      if (widgetConfiguration && widgetConfiguration.filter && widgetConfiguration.filter.rules && widgetConfiguration.filter.rules.length > 0) {
        const result: string[] = [];
        widgetConfiguration.filter.rules.forEach(x => {
          if (!(x as RuleSet).rules) {
            const rule = x as Rule;
            let condition = '';
            if (result.length > 0) {
              condition = `${widgetConfiguration.filter.condition} `;
            }
            result.push(`${condition}${rule.field} ${rule.operator} ${rule.value}`);
          }
        });
        return result;
      }
      return undefined;
    }
  );
}

export const getArrearsLabels = createSelector(
  [selectArrearsState],
  (arrearsState: ArrearsState) => {
    return arrearsState.agedDayLabels;
  });

